import { VStack } from '@genial/components';
import { useBreakpointValue } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import { Center } from 'native-base';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilState } from 'recoil';

import { DateTypes } from '../../components/FilterModal/filters/Date';
import { DateRangeTypes } from '../../components/FilterModal/filters/DateRange';
import { MultiSliderTypes } from '../../components/FilterModal/filters/MultiSlider';
import { SectionTypes } from '../../components/FilterModal/filters/Section';
import { useRecoilQuery } from '../../hooks/useRecoilQuery';
import { SignedInNavigationScreenNavigationProp } from '../../routes/types';
import { dividendHistoryFilterAtom } from '../../store/dividendHistory/atom';
import {
  dividendHistoryQuery,
  dividendHistoryQueryByDate,
} from '../../store/dividendHistory/selector';
import { calculateSliderStep } from '../../utils/calculateSliderStep';

import { DividendHistoryList } from './DividendHistoryList';
import { Header } from './Header';
import { ListHeader } from './ListHeader';
import { SearchAndFilterButton } from './SearchAndFilterButton';

export const DividendHistory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [dividendHistoryFilters, setDividendHistoryFilters] =
    useRecoilState(dividendHistoryFilterAtom);
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const isTablet = useBreakpointValue({ base: false, sm: true, lg: false });

  const defaultDaysFilter = useMemo(
    () =>
      dividendHistoryFilters.availableFilters.find(
        (filter) => filter.fieldName === 'predefinedPeriod'
      )?.selection as string[],
    [dividendHistoryFilters.availableFilters]
  );

  const hasPredefinedPeriodFilter = useMemo(
    () =>
      dividendHistoryFilters.appliedFilters.find(
        (filter) => filter.fieldName === 'predefinedPeriod'
      )?.filter as SectionTypes.SelectionProps,
    [dividendHistoryFilters.appliedFilters]
  );

  const hasDateRangeFilter = useMemo(
    () =>
      dividendHistoryFilters.appliedFilters.find((filter) => filter.fieldName === 'dateRange')
        ?.filter as DateRangeTypes.SelectionProps,
    [dividendHistoryFilters.appliedFilters]
  );

  const startDate = useMemo(() => {
    // default filter value without selection
    let days = defaultDaysFilter[0];

    if (hasPredefinedPeriodFilter && hasPredefinedPeriodFilter[0] !== '-1') {
      days = hasPredefinedPeriodFilter[0];
    }

    if (hasDateRangeFilter?.initialDate) {
      return hasDateRangeFilter.initialDate;
    }

    return new Date(Date.now() - Number(days) * 24 * 60 * 60 * 1000);
  }, [defaultDaysFilter, hasDateRangeFilter, hasPredefinedPeriodFilter]);

  const endDate = useMemo(() => {
    if (hasDateRangeFilter?.finalDate) {
      return hasDateRangeFilter.finalDate;
    }

    // default filter value without selection
    return new Date();
  }, [hasDateRangeFilter]);

  const { data: dividendHistoryBaseData } = useRecoilQuery(
    dividendHistoryQueryByDate({
      startDate,
      endDate,
    })
  );

  const getMinAssetValue = () => {
    if (dividendHistoryBaseData) {
      return Math.min(...dividendHistoryBaseData.map((item) => item.assetValue));
    }
    return 0;
  };

  const getMaxAssetValue = () => {
    if (dividendHistoryBaseData) {
      return Math.max(...dividendHistoryBaseData.map((item) => item.assetValue));
    }
    return 0;
  };

  const selectedValueFilter = dividendHistoryFilters.appliedFilters.find(
    (filter) => filter.fieldName === 'assetValue'
  )?.filter as MultiSliderTypes.RangeProps | undefined;

  const {
    data: dividendHistoryData,
    error,
    loading,
    refresher,
  } = useRecoilQuery(
    dividendHistoryQuery({
      searchTerm,
      startDate,
      endDate,
      approvalDate: dividendHistoryFilters.appliedFilters.find(
        (filter) => filter.fieldName === 'approvalDate'
      )?.filter as DateTypes.SelectionProps | undefined,
      updateDate: dividendHistoryFilters.appliedFilters.find(
        (filter) => filter.fieldName === 'updateDate'
      )?.filter as DateTypes.SelectionProps | undefined,
      dueDate: dividendHistoryFilters.appliedFilters.find(
        (filter) => filter.fieldName === 'dueDate'
      )?.filter as DateTypes.SelectionProps | undefined,
      assetValueMin: selectedValueFilter?.min,
      assetValueMax: selectedValueFilter?.max,
    })
  );

  const handleGoBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }
    navigation.navigate('AppDrawer', {
      screen: 'Cockpit',
    });
  }, [navigation]);

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  useEffect(() => {
    const newFilters = {
      availableFilters: [...dividendHistoryFilters.availableFilters],
      selectedFilters: [...dividendHistoryFilters.selectedFilters],
      appliedFilters: [...dividendHistoryFilters.appliedFilters],
    };

    const assetValueFilterOnAvailableIndex = newFilters.availableFilters.findIndex(
      (filter) => filter.fieldName === 'assetValue'
    );

    const assetValueFilterOnAvailable = newFilters.availableFilters[
      assetValueFilterOnAvailableIndex
    ] as Omit<MultiSliderTypes.Props, 'onChange' | 'filterIndex'> | undefined;

    if (assetValueFilterOnAvailable && dividendHistoryBaseData) {
      const newMin = getMinAssetValue();
      const newMax = getMaxAssetValue();
      const step = calculateSliderStep(newMin, newMax);

      const newAssetValueFilter = {
        ...assetValueFilterOnAvailable,
        min: Math.floor(newMin),
        max: Math.ceil(newMax / step) * step,
        step,
      };

      newFilters.availableFilters[assetValueFilterOnAvailableIndex] = newAssetValueFilter;
    }

    setDividendHistoryFilters(newFilters);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dividendHistoryBaseData]);

  return (
    <Center flex={1} width="full">
      <VStack
        p="inset.xs"
        flex={1}
        width="full"
        maxWidth={{ base: '100%', sm: '95%', lg: '1280px' }}
        minWidth={0}
        space="stack.xxxs">
        <Header onGoBack={handleGoBack} isMobile={isMobile} />
        <VStack
          backgroundColor={{ base: 'transparent', sm: 'neutral.white' }}
          space="stack.xxxs"
          padding={{ base: undefined, sm: 'inset.sm' }}
          borderRadius="sm"
          flex={1}>
          <SearchAndFilterButton onSearch={handleSearch} isMobile={isMobile} />
          <VStack width="full" space="stack.nano" flex={1}>
            <ListHeader isMobile={isMobile} isTablet={isTablet} />
            <DividendHistoryList
              isMobile={isMobile}
              isLoading={loading}
              handleRefresh={refresher}
              data={dividendHistoryData || []}
              error={error}
            />
          </VStack>
        </VStack>
      </VStack>
    </Center>
  );
};
